import { type ComponentProps, type ReactNode } from 'react';
import { useLingui } from '@lingui/react/macro';
import { ShieldCheck, Zap } from 'lucide-react';
import { Button } from '@worldcoo/ui/components/button';
import { DialogTrigger } from '@worldcoo/ui/components/dialog';
import { cn } from '@worldcoo/ui/lib/utils';
import { Squiggle } from '../primitives';
import { ComingSoonDialog } from '../coming-soon-dialog';
import { AppPhone } from './app-phone';

function AppleLogo(props: { className?: string }) {
	return (
		<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
			<path d="M16.365 1.43c0 1.14-.42 2.22-1.19 3.02-.83.87-2.16 1.54-3.2 1.46-.13-1.1.42-2.27 1.13-3.03.79-.85 2.19-1.47 3.26-1.45zM20.7 17.02c-.53 1.22-.78 1.77-1.46 2.85-.95 1.5-2.29 3.37-3.95 3.38-1.48.02-1.86-.96-3.86-.95-2 .01-2.42.97-3.9.95-1.66-.02-2.93-1.7-3.88-3.2-2.66-4.2-2.94-9.13-1.3-11.75 1.17-1.87 3.01-2.96 4.74-2.96 1.76 0 2.87.97 4.32.97 1.41 0 2.27-.97 4.31-.97 1.54 0 3.17.84 4.33 2.29-3.8 2.08-3.18 7.5.55 9.34z" />
		</svg>
	);
}

function PlayLogo(props: { className?: string }) {
	return (
		<svg viewBox="0 0 24 24" aria-hidden {...props}>
			<path fill="#00d3ff" d="M3.6 2.3 13.4 12 3.6 21.7c-.36-.2-.6-.6-.6-1.1V3.4c0-.5.24-.9.6-1.1z" />
			<path fill="#00f076" d="M13.4 12 3.6 2.3c.13-.07.28-.12.44-.13.3-.03.6.05.86.2L17 8.6 13.4 12z" />
			<path fill="#ffc800" d="M20.5 10.9c.65.37.65 1.32 0 1.7L17 14.6 13.4 12 17 8.6l3.5 2.3z" />
			<path fill="#ff3b30" d="M13.4 12 17 14.6l-6.1 3.5c-.26.15-.56.23-.86.2-.16-.01-.31-.06-.44-.13L13.4 12z" />
		</svg>
	);
}

/**
 * The shared `Button`, not a hand-rolled one: it is what carries the focus ring, the disabled and
 * `asChild` handling that `DialogTrigger` leans on, and the coarse-pointer transition rule.
 * `dark` is the closest variant, but its `bg-foreground` is the app palette's ink — the new site's
 * store badges are flat black — so the colour is overridden here rather than by adding a variant
 * that only this one component would ever ask for.
 */
function StoreBadge({
	label,
	logo,
	className,
	...props
}: ComponentProps<typeof Button> & { label: string; logo: ReactNode }) {
	return (
		<Button
			type="button"
			variant="dark"
			size="xl"
			className={cn('gap-2.5 rounded-full bg-black font-semibold text-white hover:bg-black/85', className)}
			{...props}
		>
			{logo}
			{label}
		</Button>
	);
}

/** One bullet of the app's promises: its icon is the bullet, so it reads as copy, not as a third button beside the stores. */
function Feature(props: { icon: ReactNode; text: string }) {
	return (
		<li className="text-ink/80 flex items-center gap-2 text-sm">
			<span className="bg-ink/10 grid size-6 shrink-0 place-items-center rounded-full">{props.icon}</span>
			{props.text}
		</li>
	);
}

/** App-download band: "Haz seguimiento de tus donaciones en tiempo real" + store badges + the app on a 3D phone. */
export function AppDownload() {
	const { t } = useLingui();
	return (
		<section className="mx-auto max-w-[1440px] px-6 py-16 md:py-32 xl:px-10">
			<div className="bg-peach grid items-center gap-10 rounded-3xl px-8 py-12 md:px-14 md:py-16 lg:grid-cols-2">
				<div className="flex flex-col gap-6">
					<h2 className="text-ink text-3xl font-medium tracking-tight md:text-5xl">
						{t`Track your donations in`} <Squiggle>{t`real time`}</Squiggle>
					</h2>
					<p className="text-ink/70 max-w-sm text-base md:text-lg">
						{t`Learn the impact of every donation and the status of each project.`}
					</p>
					{/* Beside the copy, not over the phone: its column is too narrow at every width to float them
					    around it without covering the screen the loop is showing. */}
					<ul className="flex flex-col gap-1">
						<Feature icon={<Zap className="size-3.5 fill-current" />} text={t`Certify your donation in seconds.`} />
						<Feature icon={<ShieldCheck className="size-3.5" />} text={t`Full transparency of your donation.`} />
					</ul>
					<ComingSoonDialog
						description={t`Our app will be available on the App Store and Google Play very soon. In the meantime, you can already enjoy it from your browser.`}
						link={{ label: 'app.worldcoo.com', href: 'https://app.worldcoo.com' }}
					>
						<div className="flex flex-wrap gap-3">
							<DialogTrigger asChild>
								<StoreBadge label="App Store" logo={<AppleLogo className="size-7" />} />
							</DialogTrigger>
							<DialogTrigger asChild>
								<StoreBadge label="Google Play" logo={<PlayLogo className="size-6" />} />
							</DialogTrigger>
						</div>
					</ComingSoonDialog>
				</div>

				<div className="relative mx-auto h-[26rem] w-full max-w-sm">
					{/* Contained on mobile; on md+ the phone is taller than the box and centered, so it pokes
					    out top and bottom (per Figma). It spins in once and then holds still, like the
					    danchiano /new phone: no bobbing. */}
					<div className="absolute inset-0">
						<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
							<AppPhone />
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
